import { useState } from "react";
import { toast } from "sonner";
import { core } from "@/lib/mh";
import { errText } from "@/lib/http";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { StatusLight, ModeSwitch, Input, Btn } from "@/components/mh/controls";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PROVIDERS = [{ v: "kimi", l: "Kimi (Moonshot)" }, { v: "openai", l: "OpenAI" }, { v: "anthropic", l: "Anthropic Claude" }, { v: "gemini", l: "Google Gemini" }];
const MANAGED = [{ v: "anthropic", l: "Claude" }, { v: "openai", l: "GPT" }, { v: "gemini", l: "Gemini" }];

const Pick = ({ value, onChange, items, testId, disabled, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-10 rounded-lg bg-white/[0.03] border-white/10" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="bg-[#0A0A0F] border-white/10 max-h-72">{items.map((i) => <SelectItem key={i.v} value={i.v}>{i.l}</SelectItem>)}</SelectContent>
  </Select>
);

export const ModelCard = ({ data, onSaved, canEdit, isAdmin }) => {
  const m = data.model;
  const [mode, setMode] = useState(m.mode);
  const [provider, setProvider] = useState(m.provider || "kimi");
  const [key, setKey] = useState("");
  const [model, setModel] = useState(m.model || "");
  const [models, setModels] = useState(m.models || []);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(data.usage?.limit ?? 300);
  const wrap = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };

  const save = (patch) => wrap(async () => { const r = await core.setModel(patch); onSaved(r.data); setKey(""); toast.success("Model connection saved"); });
  const test = () => wrap(async () => {
    const r = await core.testModel({ provider, api_key: key || undefined });
    if (r.data.ok) { setModels(r.data.models); toast.success(`Key works · ${r.data.models.length} models available`); } else toast.error(r.data.error);
  });
  const switchMode = (v) => { setMode(v); if (v === "managed") save({ mode: "managed" }); };
  const status = mode === "managed" ? "managed" : m.mode === "own" ? m.status : "not_connected";
  const usage = data.usage || { calls: 0, limit: 300 };

  return (
    <Panel cls="p-5 sm:p-6 space-y-5" testId="model-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><Eyebrow>AI model</Eyebrow><h3 className="font-display text-3xl mt-1">Which brain runs your agents</h3></div>
        <StatusLight status={status} testId="model-status" />
      </div>
      <ModeSwitch testId="model-mode" value={mode} onChange={switchMode} disabled={!canEdit || busy}
        options={[{ value: "managed", label: "Managed by Manahouse" }, { value: "own", label: "Client's own key" }]} />
      {mode === "managed" ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Manahouse's built-in key runs the playground and audit summaries. Usage counts toward this workspace's monthly allowance and is paid by the agency.</p>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Managed model family</span>
              <div className="mt-1.5"><Pick testId="managed-provider" value={m.managed_provider} disabled={!canEdit} items={MANAGED.map((x) => ({ v: x.v, l: `${x.l} · ${data.managed_models[x.v]}` }))} onChange={(v) => save({ mode: "managed", managed_provider: v })} /></div></div>
            <div data-testid="usage-meter"><div className="flex justify-between font-mono text-[11px] text-muted-foreground"><span>This month</span><span>{usage.calls} / {usage.limit} calls</span></div>
              <div className="h-1.5 mt-2 rounded-full bg-white/[0.06]"><div className="h-full rounded-full bg-[#D4FF3A]" style={{ width: `${Math.min(100, (usage.calls / Math.max(usage.limit, 1)) * 100)}%` }} /></div></div>
          </div>
          {isAdmin && (
            <div className="flex items-end gap-2 max-w-sm">
              <Input label="Monthly limit (agency admin)" type="number" min={0} value={limit} onChange={(e) => setLimit(e.target.value)} testId="usage-limit-input" />
              <Btn testId="usage-limit-save" onClick={() => wrap(async () => { await core.setLimit(Number(limit)); toast.success("Limit updated"); onSaved((await core.connections()).data); })}>Save</Btn>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Calls go straight to your provider and are billed to your account. Keys are encrypted at rest and never shown again in full.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Provider</span>
              <div className="mt-1.5"><Pick testId="own-provider" value={provider} disabled={!canEdit} items={PROVIDERS.map((p) => ({ v: p.v, l: p.l }))} onChange={(v) => { setProvider(v); setModels([]); setModel(""); }} /></div></div>
            <Input label={m.key_mask && provider === m.provider ? `API key (saved: ${m.key_mask})` : "API key"} type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={provider === "kimi" ? "sk-… from platform.kimi.ai" : "Paste key"} disabled={!canEdit} testId="own-api-key" />
          </div>
          {models.length > 0 && (
            <div className="max-w-md"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Model</span>
              <div className="mt-1.5"><Pick testId="own-model" value={model} disabled={!canEdit} placeholder="Choose a model" items={models.map((x) => ({ v: x, l: x }))} onChange={setModel} /></div></div>
          )}
          {m.error && m.mode === "own" && <p className="text-sm text-[#FF3B5C]" data-testid="model-error">{m.error}</p>}
          <div className="flex flex-wrap gap-2">
            <Btn testId="model-test-btn" disabled={!canEdit || busy} onClick={test}>Test</Btn>
            <Btn variant="primary" testId="model-save-btn" disabled={!canEdit || busy} onClick={() => save({ mode: "own", provider, api_key: key || undefined, model: model || undefined })}>Save connection</Btn>
            {m.key_mask && <Btn testId="model-remove-key" disabled={!canEdit || busy} onClick={() => wrap(async () => { onSaved((await core.removeKey()).data); setMode("managed"); toast.success("Key removed"); })}>Remove key</Btn>}
          </div>
        </div>
      )}
      <div className="pt-4 border-t border-white/[0.06] font-mono text-[11px] text-muted-foreground" data-testid="active-model">
        Active now: {data.active_model.source === "own" ? "client key" : "managed"} · {data.active_model.provider} · {data.active_model.model}
      </div>
    </Panel>
  );
};
